import { Box, Divider, Flex, HStack, VStack } from "@chakra-ui/react";
import { AttachmentIcon } from "@chakra-ui/icons";
import { formatBytes } from "features/Resources/Shared/attachments";
import { ShowName } from "features/Resources/Shared/Components/Fields/NameField";
import EmbeddedHtml from "components/DataDisplay/EmbeddedHtml";
import EmailRecipientTag from "components/Tag/ResourceTargetTag/EmailRecipientTag";
import { ActionTypeTag } from "design-system";
import CDS from "design-system";
import moment from "moment-timezone";
import { titleize } from "helpers/string";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";
import FailedActionBanner from "features/Resources/Shared/Components/FailedActionBanner";
import FailedActionHeaderButtons from "features/Resources/Shared/Components/FailedActionHeaderButtons";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";
import ComposeBox, { useComposeBoxColors } from "features/Resources/Shared/Components/ComposeBox";

import { Email } from "../models";
import { EmailRecipient } from "../models/EmailRecipient";

const EMAIL_STATUS_COLORS: Record<string, string> = {
  pending: "blue.400",
  delivered: "green.500",
  failed: "red.500",
  skipped: "gray.400",
  removed: "gray.400",
};

const EMAIL_STATUS_LABELS: Record<string, string> = {
  pending: "Unsent",
  delivered: "Delivered",
  failed: "Failed",
  skipped: "Skipped",
  removed: "Removed",
};

function SentAtFieldTag({ email }: { email: Email }) {
  const isDelivered = email.status === "delivered";
  const label = isDelivered ? "Sent" : "Scheduled";
  const date = isDelivered ? email.deliveredAt : email.deliverAt;
  if (!date) return null;
  const timezone = email.timezone || moment.tz.guess();
  return <CDS.FieldTag label={label}>{moment.tz(date, timezone).format("MMM D, YYYY [at] h:mm A")}</CDS.FieldTag>;
}

function RecipientChips({ recipients }: { recipients?: EmailRecipient[] }) {
  if (!recipients?.length) return null;
  return (
    <Flex gap={1} align="center" flexWrap="wrap">
      {recipients.map((recipient, index) => (
        <EmailRecipientTag key={`recipient-${index}`} recipient={recipient} disabled />
      ))}
    </Flex>
  );
}

// "Delivered Jun 17, 2026 at 12:51 PM" style line shown inside the compose body.
function statusFooterText(email: Email): string | null {
  const timezone = email.timezone || moment.tz.guess();
  const fmt = (iso?: string | Date | null) => (iso ? moment.tz(iso, timezone).format("MMM D, YYYY [at] h:mm A") : null);

  switch (email.status) {
    case "pending":
      return null;
    case "delivered": {
      const at = fmt(email.effectiveDeliverAt) ?? fmt(email.deliveredAt) ?? fmt(email.statusUpdatedAt);
      return at ? `Delivered ${at}` : null;
    }
    case "skipped": {
      const at = fmt(email.statusUpdatedAt);
      return at ? `Skipped ${at}` : null;
    }
    case "failed": {
      const at = fmt(email.attemptedAt) ?? fmt(email.statusUpdatedAt);
      return at ? `Failed ${at}` : null;
    }
    case "removed": {
      const at = fmt(email.statusUpdatedAt);
      return at ? `Removed ${at}` : null;
    }
    default:
      return null;
  }
}

interface SentEmailProps {
  email: Email;
  includeHeader?: boolean;
  includeStatus?: boolean;
  includeJourney?: boolean;
}

export default function SentEmail({ email, includeHeader = true, includeJourney = true }: SentEmailProps) {
  const isFailed = email?.status === "failed";
  const { bodyBg } = useComposeBoxColors();
  const footerText = statusFooterText(email);
  const hasCC = !!email?.cc?.length;
  const hasBCC = !!email?.bcc?.length;
  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" mb={2}>
          <HStack align="center">
            <ActionTypeTag.EmailBadge />
            <ShowName name={email?.name} />
          </HStack>
          <HStack spacing={2}>
            {isFailed && <FailedActionHeaderButtons prefixId={email.prefixId} />}
            {email && <ResourceOptionMenu prefixId={email?.prefixId} includeTemplate />}
          </HStack>
        </HStack>
      )}

      {/* Pill row */}
      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {/* Status */}
        <CDS.BasePill cursor="default" _hover={{}}>
          <Flex direction="column" gap="0">
            <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Status
            </CDS.Text10>
            <HStack spacing="1" align="center">
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={EMAIL_STATUS_COLORS[email.status] ?? "gray.400"}
                flexShrink={0}
              />
              <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3" whiteSpace="nowrap">
                {EMAIL_STATUS_LABELS[email.status ?? ""] ?? titleize(email.status ?? "")}
              </CDS.Text12>
            </HStack>
          </Flex>
        </CDS.BasePill>

        {/* Sent At / Scheduled At */}
        <SentAtFieldTag email={email} />

        {/* Onboarding Journey — last in the row */}
        {includeJourney && email.newHireJourneyId && (
          <NewHireStackedLabel journeyId={email.newHireJourneyId} emphasized />
        )}
      </Flex>

      {isFailed && (
        <FailedActionBanner
          prefixId={email.prefixId}
          errorMessage={email.errorMessage}
          actionNoun="email"
        />
      )}

      <Divider my="4" />

      <ComposeBox>
        <ComposeBox.HeaderRow label="To">
          <RecipientChips recipients={email?.to} />
        </ComposeBox.HeaderRow>
        {hasCC && (
          <ComposeBox.HeaderRow label="CC">
            <RecipientChips recipients={email?.cc} />
          </ComposeBox.HeaderRow>
        )}
        {hasBCC && (
          <ComposeBox.HeaderRow label="BCC">
            <RecipientChips recipients={email?.bcc} />
          </ComposeBox.HeaderRow>
        )}
        <ComposeBox.HeaderRow bg={bodyBg}>
          <CDS.Text14 fontWeight="bold" color="content.primary" noOfLines={2}>
            {email?.subject || "—"}
          </CDS.Text14>
        </ComposeBox.HeaderRow>
        <Box px={4} pt={3} pb={3} bg={bodyBg}>
          <EmbeddedHtml innerHtml={email.deliveredBodyHtml ?? email.bodyDisplay ?? email.body} minH="120px" emailHeadingSizes />
          {!!email.attachments?.length && (
            <VStack align="stretch" spacing={2} mt={3}>
              {email.attachments.map((attachment) => (
                <HStack
                  key={attachment.id}
                  as="a"
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  px={3}
                  py={2}
                  spacing={2}
                  _hover={{ bg: "gray.50" }}
                >
                  <AttachmentIcon color="fg.subtle" boxSize="14px" />
                  <Box minW={0}>
                    <CDS.Text14 noOfLines={1} color="fg.default">{attachment.filename}</CDS.Text14>
                    <CDS.Text12 color="fg.subtle">{formatBytes(attachment.byteSize)}</CDS.Text12>
                  </Box>
                </HStack>
              ))}
            </VStack>
          )}
          {footerText && (
            <CDS.Text10 mt={3} color="content.tertiary" textAlign="right">
              {footerText}
            </CDS.Text10>
          )}
        </Box>
      </ComposeBox>
    </VStack>
  );
}
